/**
 * Cloud device command API.
 * Prefer serial-in-URL: POST /api/devices/{serial}/command/
 * Fallback body serial: POST /api/device-commands/
 */

import { CLOUD_API_BASE } from "./cloudApi";

const commandBySerialUrl = (serial) =>
  `${CLOUD_API_BASE}/api/devices/${encodeURIComponent(String(serial))}/command/`;

const commandBodyUrl = `${CLOUD_API_BASE}/api/device-commands/`;

const readError = async (res) => {
  const data = await res.json().catch(() => ({}));
  const msg = data.detail || data.error || data.message || data.non_field_errors;
  if (Array.isArray(msg)) return msg.join(" ");
  if (typeof msg === "string" && msg.trim()) return msg;
  return `Request failed (${res.status})`;
};

/**
 * POST a device command. Uses style A (serial in URL) by default.
 * @param {string|number} serial
 * @param {object} body - action + fields (without serial_number)
 */
export const postDeviceCommand = async (serial, body = {}) => {
  if (!serial) throw new Error("Device serial is required");
  const res = await fetch(commandBySerialUrl(serial), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json().catch(() => ({}));
};

/** Style B — serial in JSON body. */
export const postDeviceCommandByBody = async (serial, body = {}) => {
  if (!serial) throw new Error("Device serial is required");
  const res = await fetch(commandBodyUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ serial_number: String(serial), ...body }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json().catch(() => ({}));
};

const bareSerial = (serial) => String(serial || "").trim().replace(/^HLINK-/i, "");

/** One sensor: HLINK-{serial}/HAECHIE/{id}; all sensors (0 / 255): HLINK-{serial}/HAECHIE/ALL. */
export const arcResetTopic = (serial, sensorId = 0) => {
  const id = Number(sensorId);
  return `HLINK-${bareSerial(serial)}/HAECHIE/${id === 0 || id === 255 ? "ALL" : id}`;
};

/** Arc reset. sensorId 0 = all sensors. */
export const sendArcReset = (serial, sensorId = 0) =>
  postDeviceCommand(serial, {
    published: true,
    topic: arcResetTopic(serial, sensorId),
    payload: { cmd: "Arc_Reset", value: 1 },
  });

/** Arc reset for several sensor IDs (parallel single-sensor commands). */
export const sendArcResetMany = async (serial, sensorIds = []) => {
  const ids = [...new Set(sensorIds.map(Number).filter((id) => Number.isFinite(id)))];
  if (!ids.length) return [];
  if (ids.length === 1 && ids[0] === 0) return [await sendArcReset(serial, 0)];
  return Promise.all(ids.map((id) => sendArcReset(serial, id)));
};

/** Clears a tripped unit: publishes Trip_State 0 for that trip sensor on HLINK-{serial}/TRIP. */
export const sendTripReset = (serial, sensorId) =>
  postDeviceCommand(serial, {
    published: true,
    topic: `HLINK-${bareSerial(serial)}/TRIP`,
    payload: { cmd: "Trip_State", sensor_id: Number(sensorId), value: 0 },
  });

/** Auto calibrate (sensor_id 0 = all). */
export const sendAutoCalibrate = (serial, sensorId = 0) =>
  postDeviceCommand(serial, { action: "auto_calibrate", sensor_id: Number(sensorId) });

export const clampThreshold = (value) => {
  const n = Math.round(Number(value) / 10) * 10;
  return Math.max(50, Math.min(1500, n));
};

export const clampIrAlarmTemp = (value) => {
  const n = Math.round(Number(value) / 5) * 5;
  return Math.max(40, Math.min(200, n));
};

/** Arc / sensor threshold (50–1500, step 10). */
export const sendThreshold = (serial, sensorId, threshold) =>
  postDeviceCommand(serial, {
    action: "threshold",
    sensor_id: Number(sensorId),
    threshold: clampThreshold(threshold),
  });

/** Same threshold for every sensor (sensor_id 0 = all). */
export const sendThresholdAll = (serial, threshold) => sendThreshold(serial, 0, threshold);

/** Many thresholds in one request. */
export const sendThresholdMany = (serial, sensors = []) =>
  postDeviceCommand(serial, {
    action: "threshold",
    sensors: sensors.map((s) => ({
      sensor_id: Number(s.sensor_id),
      threshold: clampThreshold(s.threshold),
    })),
  });

/** IR alarm temperature (40–200, step 5). sensor 0/255 = all where supported. */
export const sendIrAlarm = (serial, sensorId, temperature) =>
  postDeviceCommand(serial, {
    action: "ir_alarm",
    sensor_id: Number(sensorId),
    temperature: clampIrAlarmTemp(temperature),
  });

export const sendIrAlarmMany = (serial, sensors = []) =>
  postDeviceCommand(serial, {
    action: "ir_alarm",
    sensors: sensors.map((s) => ({
      sensor_id: Number(s.sensor_id),
      temperature: clampIrAlarmTemp(s.temperature ?? s.value ?? s.threshold),
    })),
  });
